import type { PersistedSessionState } from '../types/problems';

const TOKEN_KEY = 'hawkes_bridge_token';
const SESSION_KEY = 'hawkes_bridge_session';
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const STORAGE_WARNING_SHOWN_KEY = 'hawkes_bridge_storage_warning_shown';

// Track localStorage availability
let localStorageAvailable: boolean | null = null;

/**
 * FR-116a: Check if localStorage is available
 * Returns true if localStorage is available and working, false otherwise.
 */
export function isLocalStorageAvailable(): boolean {
  // Return cached result if already checked
  if (localStorageAvailable !== null) {
    return localStorageAvailable;
  }

  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    localStorageAvailable = true;
    return true;
  } catch {
    localStorageAvailable = false;
    return false;
  }
}

/**
 * FR-116a: Show warning toast if localStorage is unavailable
 * Only shows the warning once per session (uses sessionStorage to track).
 * Returns true if warning was shown, false if already shown or localStorage is available.
 */
export function checkStorageAndWarn(showToast: (message: string, type: 'error' | 'warning' | 'success') => void): boolean {
  if (isLocalStorageAvailable()) {
    return false;
  }

  // Check if warning was already shown this session
  try {
    if (sessionStorage.getItem(STORAGE_WARNING_SHOWN_KEY)) {
      return false;
    }
    sessionStorage.setItem(STORAGE_WARNING_SHOWN_KEY, 'true');
  } catch {
    // sessionStorage also unavailable, proceed to show warning anyway
  }

  showToast(
    'Your browser\'s storage is unavailable. Session progress cannot be saved. Practice results may be lost if you close this tab.',
    'warning'
  );

  return true;
}

export function getToken(): string | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (!isLocalStorageAvailable()) {
    return;
  }
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if a JWT token is expired by decoding and checking the exp claim (FR-048)
 * @param token The JWT token to check
 * @returns true if token is expired or invalid, false if valid
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true;
  }

  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true; // Invalid JWT format
    }

    // Decode the payload (second part)
    const payloadPart = parts[1];
    if (!payloadPart) {
      return true; // Missing payload part
    }
    const payload = JSON.parse(atob(payloadPart));

    // Check if exp claim exists
    if (!payload.exp) {
      return true; // No expiration claim, treat as expired
    }

    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();

    // Add a small buffer (30 seconds) to account for clock skew
    return currentTime >= expirationTime - 30000;
  } catch {
    // If we can't decode the token, treat it as expired
    return true;
  }
}

// Session persistence (FR-031, FR-032).
//
// The practice session is persisted in **IndexedDB**, not localStorage. The
// session pool for the browse-all 3rd-level-bid mode holds the full downloaded-hand
// set — which grows every month — and would eventually overflow
// localStorage's fixed ~5MB quota (QuotaExceededError → the persist effect threw
// and crashed the page). IndexedDB's quota is disk-based (typically GBs), so it
// accommodates unbounded growth. Semantics are unchanged: one current-session
// record, 24h expiry, cleared on logout / clear-history / session end.
//
// All helpers are async and never reject: a storage failure (private mode, disk
// pressure, a blocked upgrade) resolves to a no-op / null so the UI never breaks.

const SESSION_DB_NAME = 'hawkes_bridge';
const SESSION_STORE = 'session';
const SESSION_DB_VERSION = 1;
// Single record — the current session — under a fixed in-store key.
const SESSION_RECORD_KEY = 'current';

/** Cached open handle so we don't reopen the DB on every save (persist effect fires often). */
let sessionDbPromise: Promise<IDBDatabase | null> | null = null;

function indexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    // Accessing indexedDB can throw in some sandboxed/private contexts.
    return false;
  }
}

function openSessionDb(): Promise<IDBDatabase | null> {
  if (sessionDbPromise) {
    return sessionDbPromise;
  }
  if (!indexedDbAvailable()) {
    sessionDbPromise = Promise.resolve(null);
    return sessionDbPromise;
  }

  sessionDbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      const request = indexedDB.open(SESSION_DB_NAME, SESSION_DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(SESSION_STORE)) {
          db.createObjectStore(SESSION_STORE);
        }
      };
      request.onsuccess = () => {
        // One-time cleanup: drop any legacy localStorage session left by the
        // pre-IndexedDB implementation so it can't linger or mislead.
        try {
          localStorage.removeItem(SESSION_KEY);
        } catch {
          // ignore — token storage may be unavailable
        }
        resolve(request.result);
      };
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return sessionDbPromise;
}

/**
 * Persist the current session to IndexedDB. Best-effort: never throws.
 * @param session The session state to persist (savedAt is stamped here)
 */
export async function saveSession(session: Omit<PersistedSessionState, 'savedAt'>): Promise<void> {
  const db = await openSessionDb();
  if (!db) {
    return;
  }
  const persistedSession: PersistedSessionState = {
    ...session,
    savedAt: Date.now(),
  };
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(SESSION_STORE, 'readwrite');
      tx.objectStore(SESSION_STORE).put(persistedSession, SESSION_RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * Load the current session from IndexedDB.
 * @returns The persisted session state, or null if not found or expired
 */
export async function loadSession(): Promise<PersistedSessionState | null> {
  const db = await openSessionDb();
  if (!db) {
    return null;
  }
  const session = await new Promise<PersistedSessionState | null>((resolve) => {
    try {
      const tx = db.transaction(SESSION_STORE, 'readonly');
      const request = tx.objectStore(SESSION_STORE).get(SESSION_RECORD_KEY);
      request.onsuccess = () => resolve((request.result as PersistedSessionState | undefined) ?? null);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });

  if (!session) {
    return null;
  }

  // T059: Check if session is expired (24 hours)
  if (isSessionExpired(session)) {
    await clearSession();
    return null;
  }

  return session;
}

/**
 * Clear the current session from IndexedDB. Best-effort: never throws.
 */
export async function clearSession(): Promise<void> {
  const db = await openSessionDb();
  if (!db) {
    return;
  }
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(SESSION_STORE, 'readwrite');
      tx.objectStore(SESSION_STORE).delete(SESSION_RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/**
 * T059: Check if a session has expired (24 hours)
 * @param session The session to check
 * @returns true if session is expired
 */
export function isSessionExpired(session: PersistedSessionState | null): boolean {
  if (!session || !session.savedAt) {
    return true;
  }

  const now = Date.now();
  return now - session.savedAt > SESSION_EXPIRY_MS;
}
